import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription, switchMap, catchError, of } from 'rxjs';

// Define una interfaz general para la función de búsqueda
export type SearchFunction = (searchTerm: string) => Promise<any[]>;

@Component({
  selector: 'app-search-autocomplete',
  templateUrl: './search-autocomplete.component.html',
  styleUrls: ['./search-autocomplete.css'] // Asume que tienes un archivo CSS para los estilos
})
export class AutocompleteSearchComponent implements OnInit, OnDestroy {
  // --- Entradas del componente general ---
  @Input() searchFunction!: SearchFunction;
  @Input() displayKey: string = 'nombre';
  @Input() secondaryDisplayKey: string = 'correo';
  @Input() placeholder: string = 'Buscar...';
  @Input() minLength: number = 3;
  @Input() debounceTimeMs: number = 1000;

  // --- Salidas del componente ---
  @Output() itemSelected = new EventEmitter<any>();

  // --- Propiedades internas ---
  searchForm: FormGroup;
  data: any[] = [];
  spinnerSearch: boolean = false;
  showDropdown: boolean = false;
  private searchSubscription!: Subscription;

  constructor(private fb: FormBuilder) {
    this.searchForm = this.fb.group({
      search: ['']
    });
  }

  ngOnInit(): void {
    this.setupSearch();
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  // Configura el Reactive Form y el Observable de búsqueda
  setupSearch(): void {
    this.searchSubscription = this.searchForm.get('search')!.valueChanges
      .pipe(
        debounceTime(this.debounceTimeMs),
        distinctUntilChanged(),
        // Usamos switchMap para cancelar la petición anterior si una nueva inicia
        switchMap(searchTerm => {
          if (searchTerm && searchTerm.length >= this.minLength) {
            this.spinnerSearch = true;
            // La función searchFunction debe retornar una Promise<any[]>.
            // Usamos of([]) en caso de error para no detener el observable.
            return this.searchFunction(searchTerm).then(results => results).catch(err => {
              
              return [];
            });
          }
          // Retorna un observable vacío si no se cumple la longitud mínima
          return of([]);
        }),
        catchError((err) => {
         
          return of([]); // Retorna un observable de array vacío en caso de error
        })
      )
      .subscribe(results => {
        this.spinnerSearch = false;
        this.data = results;
        // Asegúrate de mostrar el dropdown si hay resultados
        this.showDropdown = true;
      });
  }

  onFocus(): void {
    // Si hay datos, muestra el dropdown al enfocar
    if (this.data.length > 0 || (this.searchForm.get('search')?.value?.length > 0)) {
      this.showDropdown = true;
    }
  }

  onBlur(): void {
    // Pequeño retardo para permitir el clic en un elemento del dropdown
    setTimeout(() => {
      this.showDropdown = false;
    }, 200);
  }

  onItemPointerDown(event: Event, item: any): void {
    event.preventDefault();   // evita comportamientos raros del touch / focus
    event.stopPropagation();  // por si acaso

    this.selectItem(item);
  }

  selectItem(item: any): void {
    // Emitir el elemento seleccionado
    this.itemSelected.emit(item);

    // Opcional: Actualizar el input con el valor principal del elemento
    this.searchForm.get('search')!.setValue(item[this.displayKey], { emitEvent: false });

    // Opcional: Limpiar los resultados y ocultar el dropdown
    this.data = [];
    this.showDropdown = false;
  }
}